import React, { useEffect, useRef, useState } from "react"
import {
  Button,
  Col,
  Container,
  Modal,
  ModalBody,
  Row,
  Table,
} from "reactstrap"
import leftQuote from "../../assets/images/icons/left-quote.svg"
import leftArrowIcon from "../../assets/images/icons/left-arrow-white.svg"
import posterImage from "../../assets/images/video/poster-image.png"
import IcdOverview from "../../assets/images/icons/icd-overview.svg"
import IcdOverviewChart from "../../assets/images/icons/icd-chart.svg"
import PlayButton from "../../assets/images/icons/play-button.svg"
import chartIcon from "../../assets/images/icons/chartIcon.svg"
import VerifiedInvestor from "../../assets/images/verified-investor.png"
import { Link } from "react-router-dom"
import { Swiper, SwiperSlide } from "swiper/react"
import { Autoplay, Navigation, Pagination } from "swiper/modules"
import VideoModal from "modals/videoModal"
import { buildStyles, CircularProgressbar } from "react-circular-progressbar"
import { useLazyGetDashboardReportsQuery } from "store/api/investors/reports.api"
import FullPageLoader from "components/fullPageLoader"
import { useLazyGetInvestorProfileQuery } from "store/api/investorapi/auth.api"
import { useDispatch, useSelector } from "react-redux"
import { setLoginUserData } from "store/slices/userSlice"
import RejectedKycBox from "./rejectedKycBox"
import ResolveQueryBox from "./ResolveQueryBox"
import greenCheckIcon from "../../assets/images/icons/green-check-fill.svg"
import grayCheckIcon from "../../assets/images/icons/gray-cricel.svg"
import { formatIndianCurrency, formatIndianNumberinCr } from "helpers/utils"
import ICDAgreementBox from "./icdAgreementBox"
import moment from "moment"
import { formatAmount } from "helpers/common"
import { useLazyGetKycDataQuery } from "store/api/kyc/kyc"

const getSteps = (entityType, userType) => {
  if (entityType !== "COMPANY") {
    return [
      {
        label: "Phone Verification",
        key: ["PHONE_VERIFICATION"],
        type: "step",
      },
      {
        label: "ID Verification",
        key: ["ID_VERIFICATION"],
        type: "step",
      },
      {
        label: "Additional Details",
        key: ["ADDITIONAL_DETAILS"],
        type: "step",
      },
      {
        label:
          userType === "INVESTOR" ? "Bank Verification" : "Bank Verification",
        key: ["BANK_VERIFICATION", "BANK_DETAILS"],
        type: "step",
      },
      {
        label: "Photo",
        key: ["PHOTO"],
        type: "step",
      },
      {
        label: "",
        key: ["SUBMITTED", "FINAL_STEP"],
        type: "final",
      },
    ]
  }

  return [
    {
      label: "Phone Verification",
      key: ["PHONE_VERIFICATION"],
      type: "step",
    },
    {
      label: "Proof of Identity",
      key: ["PAN_GST", "GST_DETAILS", "GST_WAIVER"],
      type: "step",
    },
    {
      label: "Additional Details",
      key: ["BUSINESS_ADDITIONAL_DETAILS"],
      type: "step",
    },
    {
      label:
        userType === "INVESTOR" ? "Bank Verification" : "Bank Verification",
      key: ["BANK_VERIFICATION", "BANK_DETAILS"],
      type: "step",
    },
    {
      label: "Authorised<br/>Person KYC",
      key: ["AUTHORISED_SIGNATORY", "ID_VERIFICATION"],
      type: "step",
    },
    {
      stepNo: 6,
      label: "Photo",
      key: ["PHOTO"],
      type: "step",
    },
    {
      stepNo: 7,
      label: "Upload <br/>Authority Letter",
      key: ["AUTHORITY_LETTER"],
      type: "step",
    },
  ]
}

const InvestorDashboard = () => {
  document.title = "ICD Dashboard | Prachay"
  const [trigger, { isFetching: isLoading }] = useLazyGetDashboardReportsQuery()
  const [getProfile, { isFetching }] = useLazyGetInvestorProfileQuery()
  const { user, userType } = useSelector((state) => state?.user)
  const dispatch = useDispatch()
  const [details, setDetails] = useState(null)
  const [activeTab, setActiveTab] = useState("active")
  const [open, setOpen] = useState(false)
  const [openKyc, setOpenKyc] = useState(false)
  const [video, setVideo] = useState("")
  const [steps, setSteps] = useState([])
  const [showKycSteps, setShowKycSteps] = useState(false)
  const [kycData, setKycData] = useState(null)
  const [getResult] = useLazyGetKycDataQuery()

  const swiperRef1 = useRef(null)
  const swiperRef2 = useRef(null)

  const handleEnter = (ref) => {
    ref.current?.swiper?.autoplay?.start()
  }

  const handleLeave = (ref) => {
    ref.current?.swiper?.autoplay?.stop()
  }

  const getData = async () => {
    try {
      const { data } = await trigger({ user_type: userType }).unwrap()
      setDetails(data)
    } catch (error) {}
  }

  useEffect(() => {
    getKycData()
  }, [])

  const getKycData = async () => {
    try {
      const { success, data } = await getResult().unwrap()
      if (success) {
        setKycData(data)
      }
    } catch (error) {}
  }

  useEffect(() => {
    getData()
    onGetProfile()
  }, [userType])

  const onGetProfile = async () => {
    try {
      const { data, success } = await getProfile().unwrap()
      if (success) {
        dispatch(
          setLoginUserData({
            user: data,
            permissions: data.permissions,
          })
        )
      }
    } catch (error) {}
  }

  useEffect(() => {
    if ((user, userType, kycData)) {
      const initialSteps = getSteps(user.entity_type, userType)
      const currentStepKey = user.kyc_status

      const addExtraSteps =
        userType === "INVESTOR" && user.entity_type === "COMPANY"

      const onboarded = kycData.onboarded && userType

      const extraSteps = addExtraSteps
        ? [
            {
              label: "Agreement Details",
              key: ["AGREEMENT_DETAILS"],
              type: "step",
            },
            {
              label: "",
              key: ["SUBMITTED", "FINAL_STEP"],
              type: "final",
            },
          ]
        : []

      const fullSteps = [...initialSteps, ...extraSteps]

      const currentIndex = fullSteps.findIndex(
        (step) => step.key && step.key.includes(currentStepKey)
      )

      const updatedSteps = fullSteps.map((item, index) => {
        if (onboarded) {
          return {
            ...item,
            icon: item.key.some((k) => kycData?.kyc_completed_steps.includes(k))
              ? greenCheckIcon
              : grayCheckIcon,
          }
        } else {
          const prevStepIndex = index - 1
          if (prevStepIndex < currentIndex) {
            return { ...item, icon: greenCheckIcon }
          } else {
            return { ...item, icon: grayCheckIcon }
          }
        }
      })

      setSteps([...updatedSteps.filter((item) => item.label)])

      const currentStatus = user.status

      setShowKycSteps(!["APPROVED", "REJECTED"].includes(currentStatus))
      setOpenKyc(user?.status !== "APPROVED")
    }
  }, [user, userType, kycData])

  const getFutureTenureText = (expirationDate) => {
    const current = moment()
    const future = moment(expirationDate)

    if (!future.isValid() || future.isBefore(current)) {
      return "0 days"
    }

    const years = future.diff(current, "years")
    const temp = current.clone().add(years, "years")

    const months = future.diff(temp, "months")
    temp.add(months, "months")

    const days = future.diff(temp, "days")

    let parts = []
    if (years > 0) parts.push(`${years} year${years > 1 ? "s" : ""}`)
    if (months > 0) parts.push(`${months} month${months > 1 ? "s" : ""}`)
    if (days > 0) parts.push(`${days} day${days > 1 ? "s" : ""}`)

    return parts.join(" ") || "0 days"
  }

  const getFundProgress = (item) => {
    const principal = Number(item?.amount)
    const remaining = Number(item?.remaining_amount)

    if (isNaN(principal) || principal <= 0) return 0
    if (isNaN(remaining) || remaining < 0) return 0

    const used = principal - remaining

    if (used <= 0) return 0

    let percent = (used / principal) * 100

    return Number.isInteger(percent) ? percent : Number(percent.toFixed(1))
  }

  if (isLoading || isFetching) return <FullPageLoader />

  if (details)
    return (
      <React.Fragment>
        <Container fluid style={{ padding: "25px 25px" }}>
          <div className="icd-dashboard-top-wrapper ">
            <div className="icd-dashboard-left">
              <div className="line-border" />
              <div className="quote-box">
                <div className="quote-image-box">
                  <img src={leftQuote} alt="" />
                </div>
              </div>
              <div
                dangerouslySetInnerHTML={{
                  __html: details?.aboutUs?.value,
                }}
              ></div>
              <div className="founder-name">
                <p>~ Girish Lakhotiya</p>
                <p> Founder & Director</p>
              </div>
            </div>
            <div className="icd-dashboard-right">
              <h2>Discover all about ICD</h2>
              <div
                className="my-2"
                style={{ overflow: "hidden", borderRadius: "10px" }}
              >
                <Swiper
                  // modules={[Navigation, Pagination, Autoplay]}
                  modules={[Navigation, Pagination]}
                  spaceBetween={30}
                  slidesPerView={1}
                  navigation={false}
                  pagination={{ clickable: true }}
                  loop={false}
                  // autoplay={{
                  //   delay: 3000,
                  //   disableOnInteraction: false,
                  // }}
                >
                  {details.videos.map((item, index) => (
                    <SwiperSlide key={`video_${index}`}>
                      <div
                        className="discover-icd-slider"
                        style={{
                          position: "relative",
                        }}
                      >
                        <div
                          style={{
                            position: "absolute",
                            left: 0,
                            top: 0,
                            right: 0,
                            bottom: 0,
                            borderRadius: "10px",
                            background: "#000",
                            opacity: 0.5,
                            zIndex: 1,
                          }}
                        />
                        <img
                          width={"100%"}
                          style={{
                            minHeight: "131px",
                          }}
                          src={item?.thumbnail_url || posterImage}
                          alt=""
                        />
                        <div
                          onClick={() => {
                            setVideo(item)
                            setOpen(true)
                          }}
                          className="play-icon"
                        >
                          <img src={PlayButton} alt="" />
                        </div>
                      </div>
                    </SwiperSlide>
                  ))}
                </Swiper>
              </div>
              <Link to="/resources/videos" className="page-link">
                Check out the complete playlist here
                <div className="arrow">
                  <img src={leftArrowIcon} alt="" />
                </div>
              </Link>
            </div>
          </div>
          <div className="icd-overview-wrapper">
            <div className="icd-overview-left">
              {(details?.icdByAdmin.length > 0 ||
                details?.icdByInvestor.length > 0) && (
                <div
                  className="icd-overview-left-inner"
                  style={{ minHeight: "300px" }}
                >
                  <h3>Demand-Supply</h3>
                  <div className="icd-overview-slider">
                    {details?.icdByAdmin.length > 0 && (
                      <div
                        className="slider-inner-box"
                        onMouseEnter={() => handleEnter(swiperRef1)}
                        onMouseLeave={() => handleLeave(swiperRef1)}
                      >
                        <Swiper
                          ref={swiperRef1}
                          modules={[Navigation, Pagination, Autoplay]}
                          spaceBetween={30}
                          slidesPerView={1}
                          navigation={false}
                          pagination={{ clickable: true }}
                          loop={true}
                          // autoplay={{
                          //   delay: 3000,
                          //   disableOnInteraction: false,
                          // }}
                        >
                          {details?.icdByAdmin.map((item, index) => (
                            <SwiperSlide key={`admin_icd_${index}`}>
                              <h4>Fund Requirement - Prachay </h4>
                              <p>Amount: ₹{item?.amount}</p>
                              <p>Return: {item?.interest_rate}% p.a.</p>
                              <div className="page-link-wrapper">
                                <Link
                                  to="/icd-investments/fund-requests"
                                  className="page-link"
                                >
                                  Know more
                                </Link>
                              </div>
                            </SwiperSlide>
                          ))}
                        </Swiper>
                      </div>
                    )}
                    {details?.icdByInvestor.length > 0 && (
                      <div
                        className="slider-inner-box"
                        onMouseEnter={() => handleEnter(swiperRef2)}
                        onMouseLeave={() => handleLeave(swiperRef2)}
                      >
                        <Swiper
                          ref={swiperRef2}
                          modules={[Navigation, Pagination, Autoplay]}
                          spaceBetween={30}
                          slidesPerView={1}
                          navigation={false}
                          pagination={{ clickable: true }}
                          loop={true}
                          // autoplay={{
                          //   delay: 3000,
                          //   disableOnInteraction: false,
                          // }}
                        >
                          {details?.icdByInvestor.map((item, index) => (
                            <SwiperSlide key={`admin_icd_${index}`}>
                              <h4>Fund Available - Investor </h4>
                              <p>Amount: ₹{item?.amount}</p>
                              <p>Return: {item?.interest_rate}% p.a.</p>
                              <div className="page-link-wrapper">
                                <Link
                                  to="/icd-investments/my-icds"
                                  className="page-link"
                                >
                                  Know more
                                </Link>
                              </div>
                            </SwiperSlide>
                          ))}
                        </Swiper>
                      </div>
                    )}
                  </div>
                  <div className="overview-wrapper-chart">
                    <img src={IcdOverviewChart} alt="" />
                  </div>
                  <div className="overview-wrapper">
                    <img src={IcdOverview} alt="" />
                  </div>
                </div>
              )}
              <div className="investment-summary-wrapper">
                <div className="investment-summary-left">
                  <h3>Fund Threshold</h3>
                  <Swiper
                    modules={[Navigation, Pagination, Autoplay]}
                    spaceBetween={20}
                    navigation={false}
                    pagination={{ clickable: true }}
                    loop={false}
                    breakpoints={{
                      640: { slidesPerView: 1 },
                    }}
                  >
                    {details?.agreements.map((item, index) => (
                      <SwiperSlide key={`admin_icds_${index}`}>
                        <div className="card-section">
                          <div
                            className="mx-auto"
                            style={{ width: 200, height: 200 }}
                          >
                            <svg style={{ height: 0 }}>
                              <defs>
                                <linearGradient
                                  id="gradientStroke"
                                  x1="0"
                                  y1="0"
                                  x2="0"
                                  y2="1"
                                >
                                  <stop offset="0%" stopColor="#035CA1" />
                                  <stop offset="100%" stopColor="#7EC0F7" />
                                </linearGradient>
                              </defs>
                            </svg>
                            <CircularProgressbar
                              value={getFundProgress(item)}
                              text={`${getFundProgress(item)}%`}
                              strokeWidth={14}
                              styles={buildStyles({
                                pathColor: "url(#gradientStroke)",
                                trailColor: "rgba(218, 225, 252, 1)",
                                textColor: "#161B2C",
                              })}
                            />
                          </div>
                        </div>
                        <h4 className="mt-5">
                          {formatIndianNumberinCr(
                            item?.amount - item?.remaining_amount
                          )}
                          /<span>{formatIndianNumberinCr(item?.amount)}</span>
                        </h4>
                        <h5>Remaining Tenure: </h5>
                        <p>{getFutureTenureText(item?.expiration_date)}</p>
                        <p style={{ height: "20px" }}></p>
                      </SwiperSlide>
                    ))}
                  </Swiper>
                </div>
                <div className="investment-summary-right">
                  <div className="d-flex align-items-center justify-content-between">
                    <h4>Investment Summary</h4>
                    <Link to="/icd-investments/my-icds">
                      View Details
                      <img className="ms-1" src={leftArrowIcon} alt="" />
                    </Link>
                  </div>

                  <div className="investments-table-wrapper">
                    <div className="tab-section">
                      <p
                        className={
                          activeTab === "active" ? "tab active" : "tab"
                        }
                        onClick={() => setActiveTab("active")}
                      >
                        Active Investments
                      </p>
                      <p
                        className={activeTab === "past" ? "tab active" : "tab"}
                        onClick={() => setActiveTab("past")}
                      >
                        Past Investments
                      </p>
                    </div>
                    <div className="tab-content">
                      {activeTab === "active" ? (
                        <div>
                          <div className="dashboard-card-table-section">
                            <Table responsive>
                              <thead>
                                <tr>
                                  <th>No.</th>
                                  <th>Amount (₹)</th>
                                  <th>Interest </th>
                                  <th>Maturity</th>
                                  <th>Interest Earned (₹)</th>
                                </tr>
                              </thead>
                              <tbody>
                                {details.activeInvestment.map((item, index) => (
                                  <tr key={`active_investment_${index}`}>
                                    <td>{index + 1}</td>
                                    <td>
                                      ₹ {formatIndianCurrency(item?.amount)}
                                    </td>
                                    <td>
                                      {formatIndianCurrency(item.interest_rate)}
                                      % p.a.
                                    </td>
                                    <td>
                                      {moment(item.repayment_date).format(
                                        "DD MMM YYYY"
                                      )}
                                    </td>
                                    <td>
                                      ₹ {formatAmount(item.interest_accrued)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <div className="dashboard-card-table-section">
                            <Table responsive>
                              <thead>
                                <tr>
                                  <th>No.</th>
                                  <th>Amount</th>
                                  <th>Interest </th>
                                  <th>Maturity</th>
                                  {/* <th>Accrued Interest</th> */}
                                </tr>
                              </thead>
                              <tbody>
                                {details.pastInvestment.map((item, index) => (
                                  <tr key={`active_investment_${index}`}>
                                    <td>{index + 1}</td>
                                    <td>
                                      ₹{formatIndianCurrency(item?.amount)}
                                    </td>
                                    <td>
                                      {formatIndianCurrency(item.interest_rate)}
                                      % p.a.
                                    </td>
                                    <td>
                                      {moment(item.repayment_date).format(
                                        "DD MMM YYYY"
                                      )}
                                    </td>
                                    {/* <td>
                                      ₹ {formatAmount(item.interest_accrued)}
                                    </td> */}
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="icd-overview-right">
              <h4 className="title">
                <span> News & Updates</span>
              </h4>
              <div className="blog-list-wrapper">
                {details.blogs.map((item, index) => (
                  <div
                    key={`blog_${index}`}
                    className="d-flex align-items-center gap-3 blog-list-inner-box"
                  >
                    <div className="chart-icon">
                      <img src={chartIcon} alt="" />
                    </div>
                    <div className="">
                      <Link to={`/resources/blog/${item?.id}`}>
                        <h6>{item.title}</h6>
                        <p>View more detail →</p>
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
              <h5>
                Stay informed about investments, regulatory changes, and company
                updates.
              </h5>
            </div>
          </div>
        </Container>
        {open && <VideoModal item={video} open={open} setOpen={setOpen} />}

        {user?.status === "ICD_AGREEMENT" ? (
          <ICDAgreementBox
            template_type="ICD_AGREEMENT"
            icd_id=""
            open={openKyc}
            setOpen={setOpenKyc}
          />
        ) : (
          <Modal
            isOpen={openKyc}
            toggle={() => setOpenKyc(!openKyc)}
            size="sm"
            centered
            style={{
              border: 0,
              minWidth: "350px",
            }}
          >
            <ModalBody
              style={{
                padding: 0,
              }}
            >
              <Row>
                <Col
                  xl={12}
                  className="investor-dashboard-wrapper"
                  style={{
                    display: "block",
                    padding: 0,
                  }}
                >
                  <div
                    className="investor-card-wrapper"
                    style={{
                      display: "block",
                      padding: 0,
                    }}
                  >
                    {user?.status === "QUERY_RAISED" ? (
                      <ResolveQueryBox />
                    ) : (
                      <>
                        {showKycSteps && (
                          <div className="start-investing-section">
                            {user?.status === "UNDER_REVIEW" ? (
                              <>
                                <h2>KYC Under Review</h2>
                                <h3>
                                  Your KYC is under review. We're verifying your
                                  details to ensure security and compliance.
                                  You'll be notified once the review is
                                  complete.
                                </h3>
                              </>
                            ) : (
                              <>
                                <h2>Get Verified to Start Investing</h2>
                                <h3>
                                  Complete your KYC in just a few steps to
                                  access all features securely and stay
                                  compliant.
                                </h3>
                              </>
                            )}
                            <div className="investor-steps-box-wrapper">
                              {steps.map((item, index) => (
                                <React.Fragment key={`steps_${index}`}>
                                  <div className="step-section-inner-box">
                                    <div className="step-section-title">
                                      <img
                                        height={"20px"}
                                        src={item.icon}
                                        alt=""
                                      />
                                      <h6
                                        dangerouslySetInnerHTML={{
                                          __html: item.label,
                                        }}
                                      ></h6>
                                    </div>
                                    <p>Step {index + 1}</p>
                                  </div>
                                </React.Fragment>
                              ))}
                            </div>
                            {user?.status === "UNDER_REVIEW" ? (
                              <Link to={"/kyc"}>
                                <Button className="btn btn-primary">
                                  KYC Under Review
                                </Button>
                              </Link>
                            ) : (
                              <Link to={"/kyc"}>
                                <Button className="btn btn-primary">
                                  Complete KYC Now
                                </Button>
                              </Link>
                            )}
                          </div>
                        )}
                      </>
                    )}

                    {!showKycSteps && user?.status === "APPROVED" && (
                      <div className="verified-investing-section">
                        <div className="verified-investing-inner-box">
                          <div>
                            <h4>Verified!</h4>
                            <p>
                              KYC verification is complete. You can now access
                              our full range of investment options.
                            </p>
                          </div>
                          <img height={"70px"} src={VerifiedInvestor} alt="" />
                        </div>
                        <div className="mt-5">
                          <Link to="/icd-investments/fund-requests">
                            <button
                              type="button"
                              className="btn btn-primary w-100"
                            >
                              Explore
                            </button>
                          </Link>
                          <Link to="/my-investments">
                            <button
                              type="button"
                              className="btn-view-investments btn-primary w-100"
                            >
                              View Investments
                            </button>
                          </Link>
                        </div>
                        {/* <h5>Verified on May 10, 2025</h5> */}
                      </div>
                    )}
                  </div>
                </Col>
              </Row>
            </ModalBody>
          </Modal>
        )}
      </React.Fragment>
    )
}

export default InvestorDashboard
